// @ts-nocheck
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns, resolveSelectValue } from '../../common/Table';

import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import RequestListButtons from './RequestListButtons';
import { RequestStatusLabel } from './RequestStatusLabel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings.ts';
import GameLink from '../../common/links/GameLink';
import UserLink from '../../common/links/UserLink';
import WorkflowLink from '../../common/links/WorkflowLink';
import requestStatusNames from '../../../constants/requestStatusNames';
import PlatformLink from '../../common/links/PlatformLink';
import { useTranslation } from 'react-i18next';
import {
  exportMyRequestData,
  getRequests,
  getRequestsMadeByMe,
} from '../../../api/requestApi';
import { useAppSelector } from '../../../hooks';

export interface RequestsMadeByMeListProps {
  reload?: any;
  handleDownloadKeys?: (e: any, row: any) => void;
  isProcessing?: boolean | undefined;
  settingsKey?: string;
  defaultPageSize?: number;
  title?: string;
  noDataComponent?: React.ReactNode;
  tableViewSwitcher?: any;
  allRequests?: boolean;
}

function RequestsMadeByMeList({
  reload,
  handleDownloadKeys,
  isProcessing,
  settingsKey,
  defaultPageSize,
  title,
  noDataComponent,
  tableViewSwitcher,
  allRequests,
}: RequestsMadeByMeListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const allColumns = [
    'reference',
    'games',
    'platform',
    'numberOfKeys',
    'keyWorkflowName',
    'requestedByFullName',
    'requestedDate',
    'currentUserProfileGroupName',
    'requestStatus',
  ];
  const suffixColumns = ['id'];
  const defaultVisibleColumns = allColumns;

  const currentUser = useAppSelector((state) => state.auth);

  const [requests, setRequests] = useState();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    settingsKey || 'requestsmadebymelist-2.13-settings',
    getTableSettingsDefaults({
      defaultSort: 'reference',
      direction: 'desc',
      defaultVisibleColumns,
    }),
    null,
    allRequests ? getRequests : getRequestsMadeByMe,
    setRequests,
    reload,
    t('Failed to load requests')
  );

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig = [
    {
      Key: 'reference',
      Label: '#',
      width: '5%',
      className: 'no-wrap',
      Render: (row) => (
        <Link to={'/request/' + row.requestGroupId}>{row.reference}</Link>
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'games',
      Label: t('Games'),
      width: '20%',
      Render: (row) => (
        <GameLink
          gameId={row.gameId}
          skuId={row.skuId}
          gameTitle={row.gameTitle}
          skuName={row.skuName}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'platform',
      Label: t('Platform'),
      width: '20%',
      Render: (row) => (
        <PlatformLink
          id={row.platformId}
          name={row.platform}
          key={`${row.id},${row.platformId}`}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'numberOfKeys',
      Label: t('Keys'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (row) => CommaNumber_NoDecimal(row.numberOfKeys),
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Type'),
      width: '20%',
      Render: (row) => (
        <WorkflowLink
          id={row.keyWorkflowId}
          key={row.keyWorkflowId}
          name={row.keyWorkflowName}
          colour={row.keyWorkflowColourHex}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestedByFullName',
      Label: t('Requested by'),
      width: '10%',
      Render: (row) => (
        <>
          <UserLink id={row.requestedById} name={row.requestedByFullName} />
          <br />
          {row.requestedByCompanyName}
        </>
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'currentUserProfileGroupName',
      Label: t('Waiting for'),
      width: '10%',
      Render: (row) => row.currentUserProfileGroupName,
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestStatus',
      Label: t('Status'),
      width: '10%',
      Render: (row) => (
        <RequestStatusLabel
          requestStatus={row.requestStatus}
          isWaitingForKeys={row.isWaitingForKeys}
          isDownloaded={row.downloaded}
          isEmailSent={row.allAccountDetailsEmailsSent}
        />
      ),
      Filter: {
        Render: (col) => {
          const rawValue = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          const value = resolveSelectValue(rawValue, requestStatusNames);
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">All</option>
              {requestStatusNames.map((x) => (
                <option key={x} value={x}>
                  {t(x)}
                </option>
              ))}
            </select>
          );
        },
      },
    },
    {
      Key: 'requestedDate',
      Label: t('Request date'),
      width: '5%',
      Render: (row) => <FormattedDate value={row.requestedDate} variant="default" />,
    },
    {
      Key: 'id',
      width: '5%',
      Label: '',
      className: 'text-right',
      Sort: false,
      Render: (row) => (
        <RequestListButtons
          row={row}
          handleDownloadKeys={handleDownloadKeys}
          isProcessing={isProcessing}
          currentUser={currentUser}
        />
      ),
    },
  ];

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);

  // return an alaska table
  return requests?.data ? (
    <KwPanel className="mb-4">
      {title && (
        <header className="mb-2">
          <h2>{title}</h2>
        </header>
      )}
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={requests.data.map((x) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={requests.totalRowCount}
        filteredRowCount={requests.filteredRowCount}
        scopes={requests.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        className="td-align-top"
        noDataComponent={noDataComponent}
        tableActionBarButtons={tableViewSwitcher}
        exportData={exportMyRequestData}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default RequestsMadeByMeList;
